import { Loader2, RefreshCw, Sparkles } from 'lucide-react';
import type { AIFeedbackEntry } from '@/services/feedbackService';

// ============================================================================
// Props
// ============================================================================

interface Props {
  feedback: AIFeedbackEntry | null;
  loading: boolean;
  onGenerate: () => void;
  experimentName?: string;
}

// ============================================================================
// Rating helpers
// ============================================================================

const ratingConfig: Record<string, { label: string; color: string; bg: string }> = {
  excellent: { label: 'Excellent', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)' },
  good: { label: 'Good', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' },
  satisfactory: { label: 'Satisfactory', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
  needs_improvement: { label: 'Needs Improvement', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
};

function getRating(rating: string) {
  return ratingConfig[rating] || ratingConfig.satisfactory;
}

// ============================================================================
// Component
// ============================================================================

export default function AIFeedbackCard({ feedback, loading, onGenerate, experimentName }: Props) {
  // ---- Loading state ----
  if (loading) {
    return (
      <div className="ai-feedback-card">
        <div className="ai-fb-loading">
          <div className="ai-fb-loading-icon">
            <Loader2 size={32} className="ai-fb-spin" />
          </div>
          <h3>Generating AI Feedback</h3>
          <p>Analyzing your experiment data{experimentName ? ` for ${experimentName}` : ''}...</p>
          <div className="ai-fb-loading-dots">
            <span className="dot" />
            <span className="dot" />
            <span className="dot" />
          </div>
        </div>
        <style>{cardStyles}</style>
      </div>
    );
  }

  // ---- Empty state (no feedback yet) ----
  if (!feedback) {
    return (
      <div className="ai-feedback-card">
        <div className="ai-fb-empty">
          <div className="ai-fb-empty-icon">
            <Sparkles size={36} />
          </div>
          <h3>AI Feedback</h3>
          <p>
            Get personalized AI-generated feedback on your experiment performance
            {experimentName ? ` for "${experimentName}"` : ''}.
          </p>
          <button className="ai-fb-generate-btn" onClick={onGenerate}>
            <Sparkles size={16} />
            Generate Feedback
          </button>
        </div>
        <style>{cardStyles}</style>
      </div>
    );
  }

  // ---- Feedback display ----
  const rating = getRating(feedback.overallRating);

  return (
    <div className="ai-feedback-card">
      {/* Header */}
      <div className="ai-fb-header">
        <div className="ai-fb-header-left">
          <Sparkles size={18} className="ai-fb-sparkle" />
          <h3>AI Feedback</h3>
        </div>
        <div
          className="ai-fb-rating-badge"
          style={{ color: rating.color, background: rating.bg, borderColor: rating.color }}
        >
          {rating.label}
        </div>
      </div>

      {/* Experiment name */}
      {feedback.experimentName && (
        <div className="ai-fb-experiment-name">
          Experiment {feedback.experimentNumber}: {feedback.experimentName}
        </div>
      )}

      {/* Feedback text */}
      <div className="ai-fb-text">
        {feedback.feedbackText.split('\n').filter(Boolean).map((paragraph, i) => (
          <p key={i}>{paragraph}</p>
        ))}
      </div>

      {/* Strengths */}
      {feedback.strengths.length > 0 && (
        <div className="ai-fb-section">
          <h4 className="ai-fb-section-title strengths-title">Strengths</h4>
          <ul className="ai-fb-list strengths-list">
            {feedback.strengths.map((item, i) => (
              <li key={i}>
                <span className="ai-fb-bullet strengths-bullet" aria-hidden="true">
                  {/* Green checkmark SVG */}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Areas for Improvement */}
      {feedback.areasForImprovement.length > 0 && (
        <div className="ai-fb-section">
          <h4 className="ai-fb-section-title improvement-title">Areas for Improvement</h4>
          <ul className="ai-fb-list improvement-list">
            {feedback.areasForImprovement.map((item, i) => (
              <li key={i}>
                <span className="ai-fb-bullet improvement-bullet" aria-hidden="true">
                  {/* Amber triangle SVG */}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div className="ai-fb-footer">
        <div className="ai-fb-meta">
          {feedback.aiModelUsed && (
            <span className="ai-fb-model">Model: {feedback.aiModelUsed}</span>
          )}
          <span className="ai-fb-date">
            {new Date(feedback.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
        <button className="ai-fb-regenerate-btn" onClick={onGenerate}>
          <RefreshCw size={14} />
          Regenerate
        </button>
      </div>

      <style>{cardStyles}</style>
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const cardStyles = `
  /* ---- Card container ---- */
  .ai-feedback-card {
    background: var(--bg-surface, #2C2C2C);
    border: 1px solid var(--border, #4A4A4A);
    border-radius: 12px;
    overflow: hidden;
    width: 100%;
  }

  /* ---- Loading state ---- */
  .ai-fb-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 48px 24px;
    gap: 8px;
  }

  .ai-fb-loading-icon {
    color: var(--primary, #FF5A36);
    margin-bottom: 8px;
  }

  .ai-fb-spin {
    animation: aiFbSpin 1s linear infinite;
  }

  @keyframes aiFbSpin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  .ai-fb-loading h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary, #FFFFFF);
  }

  .ai-fb-loading p {
    margin: 0;
    font-size: 13px;
    color: var(--text-secondary, #CCCCCC);
  }

  /* Animated dots */
  .ai-fb-loading-dots {
    display: flex;
    gap: 6px;
    margin-top: 12px;
  }

  .ai-fb-loading-dots .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--primary, #FF5A36);
    animation: aiFbPulse 1.4s ease-in-out infinite;
  }

  .ai-fb-loading-dots .dot:nth-child(2) {
    animation-delay: 0.2s;
  }

  .ai-fb-loading-dots .dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes aiFbPulse {
    0%, 80%, 100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    40% {
      opacity: 1;
      transform: scale(1);
    }
  }

  /* ---- Empty state ---- */
  .ai-fb-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 48px 24px;
    gap: 8px;
  }

  .ai-fb-empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: rgba(255, 90, 54, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary, #FF5A36);
    margin-bottom: 8px;
  }

  .ai-fb-empty h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary, #FFFFFF);
  }

  .ai-fb-empty p {
    margin: 0;
    font-size: 13px;
    color: var(--text-secondary, #CCCCCC);
    max-width: 340px;
    line-height: 1.5;
  }

  .ai-fb-generate-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--primary, #FF5A36);
    color: #FFFFFF;
    border: none;
    padding: 12px 24px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    margin-top: 12px;
    transition: all 0.2s ease;
  }

  .ai-fb-generate-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(255, 90, 54, 0.35);
  }

  .ai-fb-generate-btn:active {
    transform: translateY(0);
  }

  /* ---- Header ---- */
  .ai-fb-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border, #4A4A4A);
  }

  .ai-fb-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ai-fb-sparkle {
    color: var(--accent, #FFEA00);
  }

  .ai-fb-header h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary, #FFFFFF);
  }

  .ai-fb-rating-badge {
    padding: 4px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.3px;
    border: 1px solid;
  }

  /* ---- Experiment name ---- */
  .ai-fb-experiment-name {
    padding: 10px 20px 0;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary, #CCCCCC);
  }

  /* ---- Feedback text ---- */
  .ai-fb-text {
    padding: 16px 20px;
  }

  .ai-fb-text p {
    margin: 0 0 12px 0;
    font-size: 13.5px;
    line-height: 1.65;
    color: var(--text-primary, #FFFFFF);
  }

  .ai-fb-text p:last-child {
    margin-bottom: 0;
  }

  /* ---- Sections (strengths / improvements) ---- */
  .ai-fb-section {
    padding: 0 20px 16px;
  }

  .ai-fb-section-title {
    margin: 0 0 10px 0;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .strengths-title {
    color: #22c55e;
  }

  .improvement-title {
    color: #f59e0b;
  }

  .ai-fb-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .ai-fb-list li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 13px;
    line-height: 1.55;
    color: var(--text-primary, #FFFFFF);
  }

  .ai-fb-bullet {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
  }

  .strengths-bullet {
    background: rgba(34, 197, 94, 0.15);
    color: #22c55e;
  }

  .improvement-bullet {
    background: rgba(245, 158, 11, 0.15);
    color: #f59e0b;
  }

  /* ---- Footer ---- */
  .ai-fb-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-top: 1px solid var(--border, #4A4A4A);
    background: var(--bg-elevated, #333);
  }

  .ai-fb-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 11px;
    color: var(--text-muted, #999999);
  }

  .ai-fb-model {
    padding: 2px 8px;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 4px;
  }

  .ai-fb-regenerate-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 1px solid var(--border, #4A4A4A);
    color: var(--text-secondary, #CCCCCC);
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .ai-fb-regenerate-btn:hover {
    border-color: var(--primary, #FF5A36);
    color: var(--primary, #FF5A36);
    background: rgba(255, 90, 54, 0.08);
  }

  .ai-fb-regenerate-btn:active {
    transform: scale(0.97);
  }
`;
